import { Injectable } from '@angular/core';

/** One captured console line. */
export interface ConsoleEntry {
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  message: string;
  ts: string; // ISO timestamp
}

/** One captured network call (METADATA ONLY — never headers/bodies). */
export interface NetworkEntry {
  method: string;
  url: string;
  status: number;       // HTTP status, or 0 for a transport-level error
  durationMs: number;
  ts: string;           // ISO timestamp (request start)
}

/** A point-in-time copy of the buffers, attached to a bug report. */
export interface DiagnosticsSnapshot {
  console: ConsoleEntry[];
  network: NetworkEntry[];
  /** Only the console.error lines, kept in their own ring so chatty logging cannot push them out. */
  errors: ConsoleEntry[];
}

/**
 * App-wide diagnostics ring buffers feeding the cable-harness "Report a Bug" flow.
 *
 * Console logs and network calls can't be captured retroactively, so this service
 * records them continuously from app startup (the console wrappers are installed in
 * the constructor — instantiate it early, e.g. via an environment initializer in
 * app.config.ts). The network buffer is fed by `networkLogInterceptor`.
 *
 * Privacy: network entries are METADATA ONLY (method/url/status/duration/timestamp).
 * No request/response bodies and no headers (Authorization in particular) are ever
 * recorded here.
 */
@Injectable({ providedIn: 'root' })
export class DiagnosticsBufferService {
  /** Keep the console buffer larger than network — logs are the cheaper, denser signal. */
  private static readonly CONSOLE_CAP = 200;
  private static readonly NETWORK_CAP = 50; // covers the "last ~30 calls" requirement
  private static readonly ERROR_CAP = 50;
  /** Cap a single console line so a giant object dump can't bloat the report. */
  private static readonly MAX_MSG_LEN = 2000;

  private consoleEntries: ConsoleEntry[] = [];
  private networkEntries: NetworkEntry[] = [];
  private errorEntries: ConsoleEntry[] = [];

  private patched = false;

  constructor() {
    this.patchConsole();
  }

  /** Record a network call. Called by `networkLogInterceptor`. */
  recordNetwork(entry: NetworkEntry): void {
    this.networkEntries.push(entry);
    if (this.networkEntries.length > DiagnosticsBufferService.NETWORK_CAP) {
      this.networkEntries.shift();
    }
  }

  /** Shallow copies of the buffers for attaching to a report. */
  getSnapshot(): DiagnosticsSnapshot {
    return {
      console: [...this.consoleEntries],
      network: [...this.networkEntries],
      errors: [...this.errorEntries],
    };
  }

  /**
   * Wrap console.{log,info,warn,error,debug}: call the original (so the devtools
   * console is unchanged) then mirror the line into the ring buffer. Idempotent.
   */
  private patchConsole(): void {
    if (this.patched || typeof console === 'undefined') {
      return;
    }
    this.patched = true;

    (['log', 'info', 'warn', 'error', 'debug'] as const).forEach(level => {
      const original = (console as any)[level]?.bind(console);
      if (typeof original !== 'function') {
        return;
      }
      (console as any)[level] = (...args: any[]) => {
        original(...args);
        try {
          this.pushConsole(level, args);
        } catch {
          // Never let diagnostics capture break the app's logging.
        }
      };
    });
  }

  private pushConsole(level: ConsoleEntry['level'], args: any[]): void {
    let message = args.map(a => this.stringifyArg(a)).join(' ');
    if (message.length > DiagnosticsBufferService.MAX_MSG_LEN) {
      message = message.slice(0, DiagnosticsBufferService.MAX_MSG_LEN) + '…(truncated)';
    }
    const entry: ConsoleEntry = { level, message, ts: new Date().toISOString() };
    this.consoleEntries.push(entry);
    if (this.consoleEntries.length > DiagnosticsBufferService.CONSOLE_CAP) {
      this.consoleEntries.shift();
    }
    if (level === 'error') {
      this.errorEntries.push(entry);
      if (this.errorEntries.length > DiagnosticsBufferService.ERROR_CAP) {
        this.errorEntries.shift();
      }
    }
  }

  private stringifyArg(arg: any): string {
    if (arg instanceof Error) {
      // The stack is what makes an error report actionable; the line cap above still applies.
      // V8 stacks already start with "Name: message", so only prepend it where they do not.
      const head = `${arg.name}: ${arg.message}`;
      if (!arg.stack) {
        return head;
      }
      return arg.stack.startsWith(head) ? arg.stack : `${head}
${arg.stack}`;
    }
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  }
}
